import React from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  BarChart2,
  ShoppingCart,
  Users,
  Send,
  Settings,
  PieChart,
  Grid,
  Package,
  ClipboardList,
  Box,
  Globe,
  Building2,
  ShoppingBag,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/auth.service";

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggle }) => {
  const [activeRoute, setActiveRoute] = React.useState("dashboard");
  const { user } = useAuth();

  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => user?.roles.includes(role)) || false;
  };

  const isAdmin = hasRole("admin");
  const isDirector = hasRole("director");
  const isQS = hasRole("quantity_surveyor");
  const isProcurement = hasRole("procurement");

  // Director can see everything like admin
  const isAdminLevel = isAdmin || isDirector;

  return (
    <div
      className={cn(
        "h-screen bg-[hsl(var(--sidebar-bg))] border-r border-gray-200 transition-all",
        collapsed ? "w-[60px]" : "w-[260px]"
      )}
    >
      <div className="flex items-center gap-2 px-4 py-6">
        <Link to="/">
          <h2 className="text-orange-500 text-3xl font-bold">ELKAY</h2>
        </Link>
      </div>

      <nav className="px-2">
        <div className="sidebar-group">
          <Link
            to="/"
            className={cn(
              "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
              activeRoute === "dashboard"
                ? "bg-blue-100 text-blue-600"
                : "text-gray-600 hover:bg-gray-100"
            )}
            onClick={() => setActiveRoute("dashboard")}
          >
            <Grid size={20} />
            {!collapsed && <span>Dashboard</span>}
            {!collapsed && <ChevronDown className="ml-auto" size={16} />}
          </Link>
        </div>

        {isAdminLevel && (
          <div className="sidebar-group">
            <Link
              to="/users"
              className={cn(
                "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                activeRoute === "users"
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              onClick={() => setActiveRoute("users")}
            >
              <Users size={20} />
              {!collapsed && <span>Users</span>}
            </Link>
          </div>
        )}

        {hasAnyRole(["admin", "director", "procurement"]) && (
          <div className="sidebar-group">
            <Link
              to="/products"
              className={cn(
                "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                activeRoute === "products"
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              onClick={() => setActiveRoute("products")}
            >
              <Package size={20} />
              {!collapsed && <span>Products</span>}
            </Link>
          </div>
        )}

        {hasAnyRole(["admin", "director", "procurement"]) && (
          <div className="sidebar-group">
            <Link
              to="/suppliers"
              className={cn(
                "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                activeRoute === "suppliers"
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              onClick={() => setActiveRoute("suppliers")}
            >
              <ShoppingBag size={20} />
              {!collapsed && <span>Suppliers</span>}
            </Link>
          </div>
        )}

        {hasAnyRole(["admin", "director", "quantity_surveyor"]) && (
          <div className="sidebar-group">
            <Link
              to="/sites"
              className={cn(
                "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                activeRoute === "sites"
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              onClick={() => setActiveRoute("sites")}
            >
              <Building2 size={20} />
              {!collapsed && <span>Sites</span>}
            </Link>
          </div>
        )}

        <div className="sidebar-group">
          <Link
            to="/requests"
            className={cn(
              "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
              activeRoute === "requests"
                ? "bg-blue-100 text-blue-600"
                : "text-gray-600 hover:bg-gray-100"
            )}
            onClick={() => setActiveRoute("requests")}
          >
            <ClipboardList size={20} />
            {!collapsed && <span>Requests</span>}
          </Link>
        </div>

        {isProcurement && (
          <div className="sidebar-group">
            <Link
              to="/procurement"
              className={cn(
                "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                activeRoute === "procurement"
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              onClick={() => setActiveRoute("procurement")}
            >
              <ShoppingCart size={20} />
              {!collapsed && <span>Procurement</span>}
            </Link>
          </div>
        )}

        {isAdminLevel && (
          <div className="sidebar-group">
            <Link
              to="/analytics"
              className={cn(
                "sidebar-link flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                activeRoute === "analytics"
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              onClick={() => setActiveRoute("analytics")}
            >
              <BarChart2 size={20} />
              {!collapsed && <span>Analytics</span>}
            </Link>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;
